import React, { useRef, useState, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { googleReviews } from "./reviewsConstants";

const getTimeAgo = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  if (diffInSeconds <= 0) return "Just now";

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return diffInMinutes <= 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
  }

  const diffInHours = Math.floor(diffInSeconds / 3600);
  if (diffInHours < 24) {
    return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
  }

  const diffInDays = Math.floor(diffInSeconds / (3600 * 24));
  if (diffInDays < 7) {
    return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInDays < 30) {
    return diffInWeeks === 1 ? "1 week ago" : `${diffInWeeks} weeks ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInDays < 365) {
    return diffInMonths === 1 ? "1 month ago" : `${diffInMonths} months ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return diffInYears === 1 ? "1 year ago" : `${diffInYears} years ago`;
};

const TestimonialsSection = () => {
  const sliderRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const testimonials = googleReviews;

  const getScrollDistance = useCallback(() => {
    if (!sliderRef.current) return 350;
    const card = sliderRef.current.querySelector(".testimonial-slide-item");
    if (card) {
      return card.offsetWidth + 24;
    }
    return 350;
  }, []);

  const scrollLeft = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      const distance = getScrollDistance();
      if (scrollLeft <= 10) {
        sliderRef.current.scrollTo({ left: scrollWidth - clientWidth, behavior: "smooth" });
      } else {
        sliderRef.current.scrollBy({ left: -distance, behavior: "smooth" });
      }
    }
  };

  const scrollRight = useCallback(() => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      const distance = getScrollDistance();
      if (scrollLeft + clientWidth >= scrollWidth - 15) {
        sliderRef.current.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        sliderRef.current.scrollBy({ left: distance, behavior: "smooth" });
      }
    }
  }, [getScrollDistance]);

  const handleImageError = (index) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
  };

  // Auto-rotate slider every 4 seconds unless paused by hover/touch
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      scrollRight();
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, scrollRight]);

  return (
    <section className="section testimonials section-white" id="testimonials">
      <div className="container">
        <div className="section-header reveal-triggered fade-up active" data-aos="fade-up">
          <h2 style={{ color: "var(--text-primary)", fontWeight: 800 }}>
            What Students and Parents Say <span style={{ color: "#6366f1" }}>About Practido</span>
          </h2>
          <p>
            Thousands of students are already developing future-ready skills through project-based learning and innovation programs.
          </p>
        </div>

        <div
          className="testimonials-slider-wrapper"
          style={{ position: "relative", width: "100%", marginTop: "32px" }}
          data-aos="zoom-in"
          data-aos-delay="100"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          <button
            className="courses-scroll-btn left testimonials-nav-btn"
            onClick={scrollLeft}
            aria-label="Previous review"
            style={{
              position: "absolute",
              left: "-20px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10
            }}
          >
            <ChevronLeft size={24} />
          </button>

          <div
            className="testimonials-grid testimonials-slider-track"
            ref={sliderRef}
            style={{
              display: "flex",
              overflowX: "auto",
              scrollBehavior: "smooth",
              gap: "24px",
              padding: "12px 4px",
              scrollbarWidth: "none",
              msOverflowStyle: "none"
            }}
          >
            {testimonials.map((test, index) => (
              <div
                key={index}
                className="premium-card testimonial-card testimonial-slide-item reveal-triggered zoom-in active"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxSizing: "border-box"
                }}
              >
                <div>
                  <div
                    className="testimonial-header"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "12px"
                    }}
                  >
                    {test.avatarImg && !imageErrors[index] ? (
                      <div
                        className="testimonial-avatar"
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "50%",
                          overflow: "hidden"
                        }}
                      >
                        <img
                          src={test.avatarImg}
                          alt={test.name}
                          onError={() => handleImageError(index)}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                    ) : test.avatarInitial ? (
                      <div
                        className="testimonial-avatar"
                        style={{
                          backgroundColor: test.avatarBg || "#6366f1",
                          width: "44px",
                          height: "44px",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "18px"
                        }}
                      >
                        {test.avatarInitial}
                      </div>
                    ) : (
                      <div
                        className="testimonial-avatar"
                        style={{ backgroundColor: test.avatarBg || "#6366f1" }}
                      >
                        <img
                          src={test.avatar}
                          alt={`${test.name} - student or parent review at Practido`}
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="testimonial-info">
                      <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "bold" }}>{test.name}</h4>
                      <p style={{ margin: 0, fontSize: "12px", color: "gray" }}>
                        {test.date ? getTimeAgo(test.date) : test.time || test.role}
                      </p>
                    </div>
                  </div>
                  <div
                    className="testimonial-stars"
                    style={{ display: "flex", gap: "2px", marginBottom: "12px" }}
                  >
                    {Array.from({ length: test.rating || 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        fill="#fbbf24"
                        stroke="#fbbf24"
                        strokeWidth={2}
                      />
                    ))}
                  </div>
                  <p
                    className="testimonial-text"
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.5",
                      color: "#4b5563",
                      margin: 0
                    }}
                  >
                    {test.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <button
            className="courses-scroll-btn right testimonials-nav-btn"
            onClick={scrollRight}
            aria-label="Next review"
            style={{
              position: "absolute",
              right: "-20px",
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10
            }}
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .testimonials-slider-track::-webkit-scrollbar { display: none; }
        .testimonial-slide-item {
          flex: 0 0 calc((100% - 48px) / 3) !important;
          min-width: calc((100% - 48px) / 3) !important;
          max-width: calc((100% - 48px) / 3) !important;
        }
        @media (max-width: 992px) {
          .testimonial-slide-item {
            flex: 0 0 calc((100% - 24px) / 2) !important;
            min-width: calc((100% - 24px) / 2) !important;
            max-width: calc((100% - 24px) / 2) !important;
          }
        }
        @media (max-width: 640px) {
          .testimonial-slide-item {
            flex: 0 0 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
          }
        }
      `
        }}
      />
    </section>
  );
};

export default TestimonialsSection;

